import { Component, OnInit, ElementRef, Compiler } from '@angular/core';
import { Router } from '@angular/router';

import { OauthService } from '../services/oauth.service';
import { CDSTreeService } from '../services/cds.service';
import {UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import {CDSSystem} from "../services/capd.model";
import { Title } from '@angular/platform-browser';
import { SecureTokenService } from '../services/secure-token.service';

@Component({
  selector: 'login',
  templateUrl: './login.component.html'
})

/*
  Login page.  Pretty straightforward.
*/
export class LoginComponent implements OnInit {

  enigmaString: string;
  capdUsername: string;
  errorString: string;
  public title: string = 'M*Modal CAPD Admin';

  signInForm = new UntypedFormGroup({
    capdUsername: new UntypedFormControl(),
    enigmaString: new UntypedFormControl()
  });

  badLogin = false;
  signingIn = false;
  environmentName = "";

  constructor(private _auth: OauthService,
              private _cds: CDSTreeService,
              private _router: Router,
              private _comp: Compiler,
              private _el: ElementRef,
              private _title: Title) { }

  /**
   * On Init:
   * If the user has a token, take them to the main page.
   * Otherwise, try and get what permissions the current URL has.
   *
   * @func ngOnInit
   */
  ngOnInit() {
    // Only redirect away from login if there is a valid in-memory access token.
    // Checking sessionStorage alone is insufficient: tokens are held in memory and are
    // lost on a page refresh, so rootId in sessionStorage can outlive a valid session.
    // Without this guard the interceptor's 401-triggered navigate(['login']) causes
    // LoginComponent to immediately redirect back to the main page, creating an
    // infinite /login ↔ /main redirect loop.
    if (!!sessionStorage.getItem('rootId') && !!SecureTokenService.getAccessToken()) {
      if (sessionStorage.getItem('rootId') === '~' || sessionStorage.getItem('rootId') == null) {
        this._router.navigate(['main/mmodal']); }
      else {
        this._router.navigate(['main/company/' + sessionStorage.getItem('rootId')]);
      }
    }
    sessionStorage.setItem('permissions', 'read package-management');
    this.getEnvironment();
    this._cds.getTreeRoots().then((roots: string)  => {
      sessionStorage.setItem('treeRoots', roots);
    });
  }

  /**
   * Attempts login with given credentials
   *
   * @async
   * @func onSubmit
   */
  onSubmit(signInFormValues) {
    // Clear cache & previous failed login attempts
    this._comp.clearCache();
    sessionStorage.setItem('permissions', 'read package-management');
    this.badLogin = false;

    this.capdUsername = signInFormValues.capdUsername;
    this.enigmaString = signInFormValues.enigmaString;

    this.signInForm.reset();

    // Use jquery to temporarily disable the sign-in button
    // TODO: Figure out how to re-implement this line to gray out login: (this._el.nativeElement).find('btn_signIn').button('spinner');
    this.signingIn = true;

    this.signInForm.reset();

    // if format is incorrect, don't bother attempting login. alert user of incorrect format
    if(!this.capdUsername.includes("@")) {
      this.signingIn = false;
      // TODO: Find how to properly implement this with jquery now. (this._el.nativeElement).find('#btn_signIn').button('reset');
      this.badLogin = true;
      this.errorString = "Username is expected to be formatted as cds-username@cds-location."
      return;
    }

    // Call the login function.  On success, route to one of the main pages
    this._auth.doLogin(this.capdUsername, this.enigmaString, sessionStorage.getItem('permissions'))
          .then(() => {
            this.signInForm.reset();
            this._cds.getRootId().then(() => {
              this.signInForm.reset();
              if (sessionStorage.getItem('rootId') === '~' || sessionStorage.getItem('rootId') == null) {
                this._router.navigate(['main/mmodal']);
              } else {
                this._router.navigate(['main/company/' + sessionStorage.getItem('rootId')]);
              }
            });
            this._cds.getTreeRoots().then((roots: string)  => {
              sessionStorage.setItem('treeRoots', roots);
            });
          })

          // On the first error, try again with less permissions.
          // After that, assume that the user has failed to log in
          .catch(() => {
            sessionStorage.setItem('permissions', 'read');
            this._auth.doLogin(this.capdUsername, this.enigmaString, 'read')
                .then((res) => {
                  this.signInForm.reset();
                  // On success
                  this._cds.getRootId().then(() => {
                    if (sessionStorage.getItem('rootId') === '~' || sessionStorage.getItem('rootId') == null) {
                      this._router.navigate(['main/mmodal']);
                    } else {
                      this._router.navigate(['main/company/' + sessionStorage.getItem('rootId')]);
                    }
                  });
                })
                .catch(async (e) => {
                  this.signingIn = false;
                  // TODO: Find how to properly implement this with jquery now. (this._el.nativeElement).find('#btn_signIn').button('reset');
                  this.badLogin = true;
                  await this.badLogin
                  this.onFailedLogin(e)
                });
          });
  }

  onFailedLogin(e) {
    var default_message = "An error has occurred. Please check your connection and try again."
    try {
      if (e.status == 401) {
        if (e.error.error_description == "Username is expected to be formatted as cds-username@cds-location.")
          this.errorString = "Username is expected to be formatted as cds-username@cds-location."
        else
          this.errorString = default_message
      }
      // when we have an invalid login in GI that fails in getting UAA token, server returns only a 500 error -- default to invalid login
      else if (e.status == 500) {
        if (e.error.message.includes("I/O error on POST request for")) {
          this.errorString = "Error reaching UAA. Please check your connection and try again."
        } else if (e.error.message.includes("400")) {
          if (e.error.message.includes("Account is locked"))
            this.errorString = "Your account has been locked. Please try again later."
          else
            this.errorString = "Your username or password is incorrect. Please try again."
        } else
          this.errorString = "Your username or password is incorrect. Please try again."
      } else if (e.status == 404) {
        this.errorString = "CAPD Admin server is down. Please try again later."
      } else {
        this.errorString = default_message
      }
    } catch (e) {
      // when we hit an error, assume username/password is incorrect
      this.errorString = "Your username or password is incorrect. Please try again."
    }
  }

  getEnvironment() {
    this._cds.getCAPDAdminEnvironmentName().then((environmentName: string): void => {
      if (environmentName) {
        this.environmentName = environmentName;
        this._title.setTitle(environmentName + ' M*Modal CAPD Admin')
      } else {
        this.getEnvironmentLegacy();
      }
    });
  }

  getEnvironmentLegacy() {
    this._cds.getCdsSystem().then((system: CDSSystem)=> {
      if (system) {
        if (system.cdsType == 'test')
          this.environmentName = 'Test'
        else if (system.cdsType == 'gi')
          this.environmentName = 'AWS GI'
        else if (system.cdsType == 'ct') {
          if (system.cdsName == 'fd-atl')
            this.environmentName = 'AWS CT'
          else if (system.cdsName == 'fluencydirect')
            this.environmentName = 'AWS CT2 PIT'
        } else if (system.cdsType == 'pb') {
          if (system.cdsName == 'fd-atl')
            this.environmentName = 'AWS CPM ATL'
          else if (system.cdsName == 'fluencydirect')
            this.environmentName = "AWS CPM2 PIT"
        } else if (system.cdsType == 'ukpl')
          this.environmentName = 'AWS LCPP (London)'
        else if (system.cdsType == 'aupk')
          this.environmentName = 'AWS SCPP (Sydney)'
        else if (system.cdsType == 'capm')
          this.environmentName = 'AWS CCPP (Canada)'
        else if (system.cdsType == 'pa')
          this.environmentName = 'AWS PA (UCPP3)'
        else if (system.cdsType == 'production')
          this.environmentName = 'UAE Prod'
        else if (system.cdsType == 'depb')
          this.environmentName = 'AWS FCPP1 (Frankfurt)'
        else if (system.cdsType = 'prod') {
          if (system.cdsName == 'fd-atl')
            this.environmentName = 'Prod ATL'
          else if (system.cdsName == 'fluencydirect')
            this.environmentName = 'Prod PIT'
        } else if (system.cdsType = 'beta') {
          if (system.cdsName == 'fd-atl')
            this.environmentName = 'Beta ATL'
          else if (system.cdsName == 'fluencydirect')
            this.environmentName = 'Beta PIT'
        }
      }
      this._title.setTitle(this.environmentName + ' M*Modal CAPD Admin')

    });
  }

  loginWithSso() {
    this._auth.loginWithSso();
  }
}
